<template>
  <div class="w-full mx-auto flex overflow-hidden lens-spend-back-empty h-full">
    <div class="flex-1 flex flex-col justify-between p-13">
      <div class="flex flex-col gap-6 w-[440px] max-w-full">
        <div class="flex flex-col gap-3">
          <BaseText
            type="heading"
            size="lg"
            class="text-text-loud"
          >
            Recover Non-Compliant Ad Spend
          </BaseText>
          <div class="flex flex-col gap-3">
            <BaseText
              type="body"
              size="md"
              class="text-text-normal"
            >
              AI agents monitor your spend across your connected ad platforms to recover every overcharge.
            </BaseText>
            <BaseText
              type="body"
              size="md"
              class="text-text-normal"
            >
              Get your overages back as ad spend credit, and only pay us a % of what we get back for you.
            </BaseText>
          </div>
        </div>

        <div class="flex gap-2 items-start p-2.5 rounded-lg bg-background-normal max-w-xs">
          <DoubleCircleIcon
            class="flex-shrink-0"
            :width="20"
            :height="20"
            stroke="#303546"
          />
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            Only pay 30% of what we recover — and $0 if we recover nothing.
          </BaseText>
        </div>

        <button
          class="black-btn-primary self-start flex items-center gap-1 rounded-md pl-2.5 pr-1.5 py-1.5"
          @click="$emit('get-started')"
        >
          <BaseText
            type="label"
            size="sm"
            class="text-white"
          >
            Recover Free Ad Spend
          </BaseText>
          <ChevronIcon
            class-name="text-white transform -rotate-90"
            :width="20"
            :height="20"
          />
        </button>
      </div>

      <div class="flex gap-3 items-stretch pt-8">
        <!-- <button class="video-card flex-1 relative flex flex-col justify-end gap-3 p-3 rounded-xl overflow-hidden min-h-[128px] text-left">
          <div class="relative z-10 w-5 h-5 rounded-full border border-white border-opacity-60 flex items-center justify-center">
            <PlayIcon
              class="text-white"
              :width="12"
              :height="12"
            />
          </div>
          <div class="relative z-10 flex flex-col gap-1">
            <BaseText
              type="label"
              size="sm"
              class="text-white"
            >
              Watch the Video
            </BaseText>
            <BaseText
              type="body"
              size="sm"
              class="text-white text-opacity-70"
            >
              Learn how ad spend recovery works
            </BaseText>
          </div>
        </button> -->

        <!-- <div class="flex-1 flex flex-col gap-3 p-3 rounded-xl bg-white border border-border-normal">
          <div class="flex items-center">
            <div class="w-[18px] h-[18px] rounded-full bg-neutral-200 border border-white -mr-1.5" />
            <div class="w-[18px] h-[18px] rounded-full bg-neutral-300 border border-white" />
          </div>
          <div class="flex flex-col gap-1">
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              1:1 Recovery Support
            </BaseText>
            <BaseText
              type="body"
              size="sm"
              class="text-text-normal"
            >
              Let us set up your recovery system.
            </BaseText>
          </div>
        </div> -->
      </div>
    </div>

    <div class="flex-1 flex flex-col gap-10 p-13 border-l border-border-normal">
      <BaseText
        type="heading"
        size="lg"
        class="text-text-loud"
      >
        How much ad spend can you recover?
      </BaseText>

      <div class="flex flex-col gap-3 w-full">
        <div class="flex items-center justify-between w-full">
          <div class="flex items-center gap-2 p-1.5 rounded-md bg-white">
            <img
              v-if="lens.avatar"
              :src="lens.avatar"
              class="w-5 h-5 rounded-md flex-shrink-0"
              alt="Lens avatar"
            >
            <div
              v-else
              class="w-5 h-5 flex items-center justify-center bg-neutral-400 flex-shrink-0"
              style="border-radius: 4.5px;"
            >
              <BaseText
                type="label"
                size="xs"
                class="text-background-hover"
                style="font-size: 10px;"
              >
                {{ lensInitials }}
              </BaseText>
            </div>
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              {{ lens.name || 'Lens' }}
            </BaseText>
          </div>
          <div class="flex items-center justify-center px-1.5 py-1 rounded-md bg-background-normal">
            <BaseText
              type="label"
              size="xs"
              class="text-text-muted"
            >
              Last 30 Days
            </BaseText>
          </div>
        </div>

        <!-- Skeleton account rows while loading -->
        <template v-if="loading">
          <div
            v-for="n in 3"
            :key="`skeleton-account-${n}`"
            class="flex items-center h-11 p-1.5 rounded-xl bg-white border border-border-normal"
          >
            <div class="w-8 h-8 p-1.5 flex-shrink-0">
              <div class="skeleton w-5 h-5 rounded-md" />
            </div>
            <div class="flex flex-1 min-w-0 items-center justify-between gap-2 p-1.5">
              <div
                class="skeleton h-4 rounded-md"
                :style="{ width: `${120 + n * 30}px` }"
              />
              <div class="skeleton h-4 w-20 rounded-md flex-shrink-0" />
            </div>
          </div>
        </template>

        <!-- Actual account rows -->
        <div
          v-for="account in accounts"
          v-else
          :key="account.ad_account_id"
          class="flex items-center h-11 p-1.5 rounded-xl bg-white border border-border-normal"
        >
          <div class="flex items-center justify-center w-8 h-8 p-1.5 rounded-md flex-shrink-0">
            <component
              :is="getPlatformIcon(account.provider)"
              :width="20"
              :height="20"
            />
          </div>
          <div class="flex flex-1 min-w-0 items-center gap-2 p-1.5">
            <BaseText
              type="label"
              size="sm"
              class="flex-1 min-w-0 text-text-muted truncate"
            >
              {{ account.ad_account_name }}
            </BaseText>
            <BaseText
              type="label"
              size="sm"
              class="flex-shrink-0 text-text-loud"
            >
              {{ formatCurrency(account.spend, account.currency) }}
            </BaseText>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-3 items-center w-full">
        <div class="w-full py-2">
          <BaseSlider
            v-model="recoveryRate"
            :min="MIN_RECOVERY_RATE"
            :max="MAX_RECOVERY_RATE"
            :step="0.1"
            :disabled="loading"
            :show-bubble="!loading"
            :format-value="formatPercent"
          />
        </div>
        <BaseText
          type="body"
          size="xs"
          class="text-text-normal w-full"
        >
          Foreplay + Spendback averages 3-8% ad spend recovery on auto pilot.
        </BaseText>
      </div>

      <!-- Recovery estimate card -->
      <div class="flex flex-col rounded-2xl bg-white border border-border-normal overflow-hidden">
        <div class="flex items-center gap-1 w-full">
          <div class="flex-1 min-w-0 flex items-start gap-2 justify-between p-3 rounded-md">
            <BaseText
              type="body"
              size="sm"
              class="min-w-0 text-text-muted truncate"
            >
              Average Recovery
            </BaseText>
            <div
              v-if="loading"
              class="skeleton h-4 w-16 rounded-md flex-shrink-0"
            />
            <BaseText
              v-else
              type="label"
              size="sm"
              class="flex-shrink-0 whitespace-nowrap text-secondary-green-200"
            >
              {{ formatCurrency(estimatedRecovery) }}
            </BaseText>
          </div>
          <div class="w-px h-4 bg-border-normal flex-shrink-0" />
          <div class="flex-1 min-w-0 flex items-start gap-2 justify-between p-3 rounded-md">
            <BaseText
              type="body"
              size="sm"
              class="min-w-0 text-text-muted truncate"
            >
              Performance Fee
            </BaseText>
            <div
              v-if="loading"
              class="skeleton h-4 w-14 rounded-md flex-shrink-0"
            />
            <BaseText
              v-else
              type="label"
              size="sm"
              class="flex-shrink-0 whitespace-nowrap text-text-subdued"
            >
              {{ formatCurrency(performanceFee) }}
            </BaseText>
          </div>
        </div>
        <div class="w-full h-px bg-border-normal" />
        <div class="flex flex-col gap-2 p-3">
          <BaseText
            type="body"
            size="sm"
            class="text-text-muted"
          >
            Net Recovery /month
          </BaseText>
          <div class="flex items-center gap-2">
            <div
              class="w-6 h-6 flex items-center justify-center border-2 rounded-full border-secondary-green-200 flex-shrink-0"
            >
              <PlusIcon
                class="text-secondary-green-200 w-full h-full"
              />
            </div>
            <div
              v-if="loading"
              class="skeleton h-6 w-28 rounded-md"
            />
            <BaseText
              v-else
              type="display"
              size="2xs"
              class="text-secondary-green-200"
            >
              {{ formatCurrency(netRecovery) }}
            </BaseText>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getPlatformIcon } from '@/utils/lens/dataSourceUtils'
import { formatNumberLabel } from '@/utils/lens/formatNumberLabel'
import BaseText from '../../globals/BaseText.vue'

import DoubleCircleIcon from '../../globals/Icons/DoubleCircleIcon.vue'
import PlayIcon from '../../globals/Icons/PlayIcon.vue'
import ChevronIcon from '../../globals/Icons/ChevronIcon.vue'
import PlusIcon from '../../globals/Icons/PlusIcon.vue'

export default {
  name: 'LensSpendBackEmptyState',
  components: {
    BaseText,
    DoubleCircleIcon,
    PlayIcon,
    ChevronIcon,
    PlusIcon
  },
  props: {
    lens: {
      type: Object,
      default: () => ({})
    },
    accounts: {
      type: Array,
      default: () => []
    },
    totalMonthlySpend: {
      type: Number,
      default: 0
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    const PERFORMANCE_FEE_RATE = 0.3

    return {
      PERFORMANCE_FEE_RATE,
      MIN_RECOVERY_RATE: 3,
      MAX_RECOVERY_RATE: 8,
      recoveryRate: 4
    }
  },
  computed: {
    lensInitials () {
      if (!this.lens?.name) return ''
      const initials = this.lens.name.split(' ').map(word => word[0]).join('').toUpperCase()
      return initials.substring(0, 2)
    },
    totalSpend () {
      return this.totalMonthlySpend || this.accounts.reduce((sum, account) => sum + (Number(account.spend) || 0), 0)
    },
    estimatedRecovery () {
      return this.totalSpend * (this.recoveryRate / 100)
    },
    performanceFee () {
      return this.estimatedRecovery * this.PERFORMANCE_FEE_RATE
    },
    netRecovery () {
      return this.estimatedRecovery - this.performanceFee
    }
  },
  methods: {
    getPlatformIcon (platform) {
      return getPlatformIcon(platform)
    },
    formatPercent (value) {
      return `${Number(Number(value).toFixed(1))}%`
    },
    formatCurrency (value, currency) {
      return formatNumberLabel(Number(value) || 0, 'currency', currency)
    }
  }
}
</script>

<style scoped>
.p-13 {
  padding: 52px;
}

.lens-spend-back-empty {
  min-height: 640px;
  max-width: 1500px;
}

/* Watch the Video card — placeholder thumbnail background */
.video-card {
  background: linear-gradient(253deg, rgba(0, 0, 0, 0) 1%, rgba(0, 0, 0, 0.9) 54%), #1a1a1a;
}
</style>
